import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {isEqual, isNil} from 'lodash-es';

@Component({
    selector: 'app-select-input',
    templateUrl: './select-input.component.html',
    styleUrls: ['./select-input.component.scss'],
    standalone: false
})
export class SelectInputComponent implements OnInit, OnChanges {

  allValue = MultiselectValue.ALL;
  noneValue = MultiselectValue.NONE;
  defaultValue = MultiselectValue.DEFAULT;

  @Input() controlName: string;
  @Input() controlLabel: string;
  @Input() formGroup: FormGroup;
  @Input() model: any;
  @Input() options: any[] = [];
  @Input() optionIdKey = 'id';
  @Input() optionValueKey = 'name';
  @Input() multiple = false;
  @Input() showResetButton = false;

  @Input() showAllOption = false;
  @Input() showNoneOption = false;
  @Input() showDefaultOption = false;

  @Input() showAllLabel = 'All';
  @Input() showNoneLabel = 'None';
  @Input() showDefaultLabel = 'Default';


  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() openedChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() modelChange: EventEmitter<any> = new EventEmitter<any>();

  multipleSelection: MultiSelectSelection;

  get isRequiredField() {
    const field = this.formGroup.get(this.controlName);

    if (field.validator) {
      return field.validator({} as AbstractControl).required;
    }
  }

  get isOptionObject() {
    const firstOption = this.options && this.options[0];

    return typeof firstOption === 'object';
  }

  ngOnInit() {
    if (this.multiple) {
      if (this.formGroup) {
        const control = this.formGroup.controls[this.controlName];
        this.multipleSelection = new MultiSelectSelection(control);
      } else {
        throw new Error('Please use formGroup attribute for multiple selection');
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.multiple && this.formGroup && this.optionsChanged(changes)) {
      const control = this.formGroup.controls[this.controlName];
      const formValue = {};

      if (this.isSetToAll(control.value)) {
        formValue[this.controlName] = [MultiselectValue.ALL];
      } else if (this.isSetToNone()) {
        formValue[this.controlName] = [MultiselectValue.NONE];
      } else if (this.isSetToDefault()) {
        formValue[this.controlName] = [MultiselectValue.DEFAULT];
      }
      this.formGroup.patchValue(formValue);
    }
  }

  onSelectionChanged(event) {
    this.selectionChange.emit(event);
    this.modelChange.emit(event.value);
  }

  onOpenedChanged(event) {
    this.openedChange.emit(event);
  }

  isOptionDisabled(id: any) {
    if (this.multiple && this.formGroup) {
      this.multipleSelection.refreshAllSelection();
      return this.multipleSelection.isOptionDisabled(id);
    }
  }

  hasValidValue(): boolean {
    return this.fieldValue() !== '' && !isNil(this.fieldValue());
  }

  resetFieldValue(event: any): void {
    event.stopPropagation();

    let value: any = {};
    value[this.controlName] = null;

    this.formGroup.patchValue(value);
  }

  private optionsChanged(changes: SimpleChanges) {
    return changes.options && changes.options.currentValue && !isEqual(changes.options.currentValue, changes.options.previousValue);
  }

  private isSetToNone(): boolean {
    return !this.options?.length && this.showNoneOption;
  }

  private isSetToDefault(): boolean {
    return !this.options?.length && this.showDefaultOption;
  }

  private isSetToAll(value: any[]): boolean {
    return this.options?.length > 0 && (value?.length === 0 || value.includes(-2));
  }

  private fieldValue(): any {
    if (this.controlName && this.formGroup) {
      return this.formGroup.get(this.controlName).value;
    }
  }
}

export class MultiSelectSelection {

  constructor(
    private formControl: AbstractControl,
    private allItemsSelected = false,
    private noneItemsSelected = false,
    private defaultItemSelected = false,
  ) {
    this.updateSelection();
  }

  refreshAllSelection() {
    this.allItemsSelected = this.formControl.value?.includes(-1);
  }

  toggleAllSelection() {
    this.allItemsSelected = !this.allItemsSelected;
    if ((this.allItemsSelected && this.noneItemsSelected) || (this.allItemsSelected && this.defaultItemSelected)) {
      this.noneItemsSelected = false;
      this.defaultItemSelected = false;
    }
    this.updateSelection();
  }

  toggleNoneSelection() {
    this.noneItemsSelected = !this.noneItemsSelected;
    if ((this.noneItemsSelected && this.allItemsSelected) || (this.noneItemsSelected && this.defaultItemSelected)) {
      this.allItemsSelected = false;
      this.defaultItemSelected = false;
    }
    this.updateSelection();
  }

  toggleDefaultSelection() {
    this.defaultItemSelected = !this.defaultItemSelected;
    if ((this.defaultItemSelected && this.allItemsSelected) || (this.defaultItemSelected  && this.noneItemsSelected)  ) {
      this.allItemsSelected = false;
      this.noneItemsSelected = false;
    }
    this.updateSelection();
  }

  updateSelection() {
    if (this.allItemsSelected) {
      this.formControl
        .patchValue([MultiselectValue.ALL]);
    } else if (this.noneItemsSelected) {
      this.formControl
        .patchValue([MultiselectValue.NONE]);
    } else if (this.defaultItemSelected) {
      this.formControl
        .patchValue([MultiselectValue.DEFAULT]);
    } else {
      this.formControl.patchValue([]);
    }
  }

  isOptionDisabled(id: any) {
    return this.allItemsSelected || this.noneItemsSelected || this.defaultItemSelected;
  }
}

export enum MultiselectValue {
  ALL = -1,
  NONE = -2,
  DEFAULT = -3,
}


